import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { Box, Button, Stack, Typography } from '@mui/material';
import { LinkedinTypes } from 'enums';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { LinkedinAccountSyncStages } from '../../linkedin-accounts.types';
import { SyncProcessingStatuses } from '../app-sync-dialog.types';
import ConnectProgress from '../connect-progress';
import StepLayout from '../step-layout';
import ChooseContract from './choose-contract';
import Enter2fa from './enter-2fa';
import SolveCaptcha from './solve-captcha';
import ThirdPartyClick from './third-party-click';
import ThirdPartyText from './third-party-text';

interface Props {
  isStarterExperience: boolean;
  isLogoutMode?: boolean;
  syncStage: LinkedinAccountSyncStages | undefined;
  statusMessage?: string;
  status: SyncProcessingStatuses;
  liProductType: LinkedinTypes;
  /** Region der festen IP, für den ersten Punkt des Fortschritts. */
  locationLabel?: string;

  verificationCode: string;
  verificationLoading: boolean;
  twoFaScreenshot?: string | null;

  captcha: string | null;
  markerPosition: { x: number; y: number; percentageX: number; percentageY: number } | null;
  getCaptchaLoading: boolean;
  solveCaptchaLoading: boolean;

  thirdPartyScreenshot: string | null;
  thirdPartyText: string;
  thirdPartyTextPlaceholder: string | undefined;
  thirdPartyTextAriaLabel: string | undefined;
  thirdPartyTextType: string | undefined;
  getThirdPartyScreenshotLoading: boolean;
  thirdPartyClickLoading: boolean;

  linkedinContracts: string[];
  selectedContract: string | null;
  updateLoading: boolean;

  handleVerificationCodeChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleVerifyLinkedinAccountFromAppSync(mode?: 'pin' | 'approved'): void;

  handleImageClick(event: React.MouseEvent): void;

  handleDeleteMarker(event: React.MouseEvent): void;

  handleSolveCaptcha(): void;

  handleThirdPartyClick(): void;

  handleThirdPartyTextChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSetThirdPartyTextFromAppSync(): void;

  handleSelectContract(contract: string): () => void;

  handleAddContractFromAppSync(): void;

  onResetAccount?(): void;
}

const useElapsedSeconds = () => {
  const startRef = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    startRef.current = Date.now();
    setElapsed(0);
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const m = Math.floor(elapsed / 60);
  const s = elapsed % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

/**
 * Detect status messages where the backend is blocked waiting on the user to
 * take action outside of our UI (open the LinkedIn mobile app, approve a push
 * notification, click a magic link, ...). These flows can easily take 30+
 * seconds during which the stepper has no new stage to advance to. We surface
 * them as a prominent callout so the user understands that we are waiting on
 * them and the system has not frozen.
 */
const detectWaitingForUser = (rawMessage?: string | null, parsedMessage?: string | null) => {
  const haystack = `${rawMessage || ''} ${parsedMessage || ''}`.toLowerCase();
  if (!haystack.trim()) return null;

  if (haystack.includes('app push') || haystack.includes('push notification') || haystack.includes('push approval')) {
    return 'appPush' as const;
  }
  if (haystack.includes('magic link')) {
    return 'magicLink' as const;
  }
  return null;
};

const SyncProgress: React.FC<{
  syncStage: LinkedinAccountSyncStages | undefined;
  status: SyncProcessingStatuses;
  liProductType: LinkedinTypes;
  locationLabel?: string;
  statusMessage?: string;
}> = ({ syncStage, status, liProductType, locationLabel, statusMessage }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const elapsed = useElapsedSeconds();

  const { detailText, waitingKind } = React.useMemo(() => {
    if (!statusMessage) return { detailText: null as string | null, waitingKind: null };
    let message: string | null = null;
    try {
      const parsed = JSON.parse(statusMessage);
      message = parsed.message || parsed.userAction || null;
      // Prefer what the backend states over what its wording implies. The prose match
      // below stays as a fallback for records written before those flags existed.
      const declaredKind = parsed.awaitingApproval === true
        ? parsed.hasMagicLink === true
          ? ('magicLink' as const)
          : ('appPush' as const)
        : null;
      return {
        detailText: message,
        waitingKind: declaredKind ?? detectWaitingForUser(statusMessage, message),
      };
    } catch {
      return { detailText: statusMessage, waitingKind: detectWaitingForUser(statusMessage, statusMessage) };
    }
  }, [statusMessage]);

  const waitingCopy = React.useMemo(() => {
    if (waitingKind === 'appPush') {
      return {
        title: t('syncWaitingAppPushTitle', 'Waiting for you on your LinkedIn mobile app'),
        body: t(
          'syncWaitingAppPushBody',
          'Please open the LinkedIn app on your phone and approve the login prompt. We will continue automatically as soon as you tap "Yes, it’s me".',
        ),
      };
    }
    if (waitingKind === 'magicLink') {
      return {
        title: t('syncWaitingMagicLinkTitle', 'Waiting for you to approve the magic link'),
        body: t(
          'syncWaitingMagicLinkBody',
          'LinkedIn sent a sign-in link to your inbox. Open it on any device to approve the login — we will continue automatically.',
        ),
      };
    }
    return null;
  }, [waitingKind, t]);

  return (
    <>
      <ConnectProgress syncStage={syncStage} status={status} liProductType={liProductType} locationLabel={locationLabel} />
      {waitingCopy && (
        <Box className={classes.waitingCallout}>
          <PhoneIphoneIcon className={classes.waitingIcon} />
          <Stack>
            <Typography className={classes.waitingTitle}>{waitingCopy.title}</Typography>
            <Typography className={classes.waitingBody}>{waitingCopy.body}</Typography>
          </Stack>
        </Box>
      )}
      {!waitingCopy && detailText && (
        <Typography className={classes.detailText} mt={'16px'}>
          {detailText}
        </Typography>
      )}
      <Typography className={classes.elapsedText} mt={'8px'}>
        {t('syncElapsedTime', 'Läuft seit {{elapsed}}', { elapsed })}
      </Typography>
    </>
  );
};

const SyncProcessing: React.FC<Props> = ({
  isLogoutMode = false,
  syncStage,
  statusMessage,
  status,
  liProductType,
  locationLabel,
  verificationCode,
  verificationLoading,
  twoFaScreenshot,
  captcha,
  markerPosition,
  getCaptchaLoading,
  solveCaptchaLoading,
  thirdPartyScreenshot,
  thirdPartyText,
  thirdPartyTextPlaceholder,
  thirdPartyTextAriaLabel,
  thirdPartyTextType,
  getThirdPartyScreenshotLoading,
  thirdPartyClickLoading,
  linkedinContracts,
  selectedContract,
  updateLoading,
  handleVerificationCodeChange,
  handleVerifyLinkedinAccountFromAppSync,
  handleImageClick,
  handleDeleteMarker,
  handleSolveCaptcha,
  handleThirdPartyClick,
  handleThirdPartyTextChange,
  handleSetThirdPartyTextFromAppSync,
  handleSelectContract,
  handleAddContractFromAppSync,
  onResetAccount,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  switch (status) {
    case SyncProcessingStatuses.TWO_FA:
      return (
        <Enter2fa
          statusMessage={statusMessage}
          liProductType={liProductType}
          verificationCode={verificationCode}
          verificationLoading={verificationLoading}
          twoFaScreenshot={twoFaScreenshot}
          handleVerificationCodeChange={handleVerificationCodeChange}
          handleVerifyLinkedinAccountFromAppSync={handleVerifyLinkedinAccountFromAppSync}
          onResetAccount={onResetAccount}
        />
      );
    case SyncProcessingStatuses.CAPTCHA:
      return (
        <SolveCaptcha
          captcha={captcha}
          markerPosition={markerPosition}
          getCaptchaLoading={getCaptchaLoading}
          solveCaptchaLoading={solveCaptchaLoading}
          handleImageClick={handleImageClick}
          handleDeleteMarker={handleDeleteMarker}
          handleSolveCaptcha={handleSolveCaptcha}
        />
      );
    case SyncProcessingStatuses.THIRD_PARTY:
      return (
        <ThirdPartyClick
          thirdPartyScreenshot={thirdPartyScreenshot}
          markerPosition={markerPosition}
          getThirdPartyScreenshotLoading={getThirdPartyScreenshotLoading}
          thirdPartyClickLoading={thirdPartyClickLoading}
          handleImageClick={handleImageClick}
          handleDeleteMarker={handleDeleteMarker}
          handleThirdPartyClick={handleThirdPartyClick}
        />
      );
    case SyncProcessingStatuses.THIRD_PARTY_TEXT:
      return (
        <ThirdPartyText
          thirdPartyText={thirdPartyText}
          thirdPartyScreenshot={thirdPartyScreenshot}
          thirdPartyTextPlaceholder={thirdPartyTextPlaceholder}
          thirdPartyTextAriaLabel={thirdPartyTextAriaLabel}
          thirdPartyTextType={thirdPartyTextType}
          thirdPartyClickLoading={thirdPartyClickLoading}
          handleThirdPartyTextChange={handleThirdPartyTextChange}
          handleSetThirdPartyTextFromAppSync={handleSetThirdPartyTextFromAppSync}
        />
      );
    case SyncProcessingStatuses.CHOOSE_CONTRACT:
      return (
        <ChooseContract
          linkedinContracts={linkedinContracts}
          selectedContract={selectedContract}
          updateLoading={updateLoading}
          handleSelectContract={handleSelectContract}
          handleAddContractFromAppSync={handleAddContractFromAppSync}
        />
      );
    default:
      return (
        <StepLayout
          title={isLogoutMode ? t('appSyncLogoutTitle', 'Wir melden dein Konto ab') : t('appSyncConnectTitle', 'Wir verbinden dein Konto')}
          description={
            isLogoutMode
              ? t('logoutProcessing', 'Logging out of LinkedIn…')
              : t('appSyncConnectDescription', 'Das läuft automatisch. Du musst nichts tun, bis wir dich um deinen Code bitten.')
          }
          footer={
            !isLogoutMode && onResetAccount ? (
              <Stack alignItems={'center'}>
                <Button
                  variant={'text'}
                  color={'error'}
                  size={'small'}
                  onClick={onResetAccount}
                  sx={{ fontSize: '0.78rem', opacity: 0.7, textTransform: 'none' }}
                >
                  {t('resetAccountLogin', 'Cancel & reset account')}
                </Button>
                <Typography className={classes.resetHint}>
                  {t('resetAccountLoginHint', 'This stops the current login. You can start a new login afterwards.')}
                </Typography>
              </Stack>
            ) : undefined
          }
        >
          {isLogoutMode ? null : (
            <SyncProgress
              syncStage={syncStage}
              status={status}
              liProductType={liProductType}
              locationLabel={locationLabel}
              statusMessage={statusMessage}
            />
          )}
        </StepLayout>
      );
  }
};

const useStyles = makeStyles()((theme) => ({
  waitingCallout: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    marginTop: '20px',
    padding: '16px 20px',
    borderRadius: 12,
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.primary.main + '14',
    animation: '$waitingPulse 2.4s ease-in-out infinite',
  },
  '@keyframes waitingPulse': {
    '0%, 100%': { boxShadow: `0 0 0 0 ${theme.palette.primary.main}33` },
    '50%': { boxShadow: `0 0 0 8px ${theme.palette.primary.main}00` },
  },
  waitingIcon: {
    fontSize: theme.typography.pxToRem(32),
    color: theme.palette.primary.main,
    flexShrink: 0,
    marginTop: 2,
  },
  waitingTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 700,
    color: theme.palette.primary.main,
    marginBottom: 4,
  },
  waitingBody: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.dark,
    lineHeight: 1.5,
  },
  detailText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    lineHeight: 1.5,
  },
  elapsedText: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.disabled,
  },
  resetHint: {
    fontSize: theme.typography.pxToRem(11.5),
    color: theme.palette.text.disabled,
    marginTop: '4px',
    textAlign: 'center',
  },
}));

export default SyncProcessing;
